import { FeatureFlagsEnum } from '@/generated/featureFlags.enum';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, type ActivatedRouteSnapshot } from '@angular/router';
import { AuthService } from '@core/services/auth/auth.service';
import { FeatureFlagService } from '@core/services/feature-flag/feature-flag.service';
import { filter } from 'rxjs';
import { IaraFloatingChatPanelComponent } from './components/iara-floating-chat-panel/iara-floating-chat-panel.component';
import { IaraFloatingLauncherComponent } from './components/iara-floating-launcher/iara-floating-launcher.component';
import { IARA_FLOATING_CHAT_ROUTE_PREFIX } from './iara-floating-chat.constants';
import { IaraPanelStateService } from './services/iara-panel-state.service';

/**
 * "Pergunte à Iara" em qualquer tela: o orbe flutuante e o gate de disponibilidade.
 *
 * Montado UMA vez no shell (`app.component.html`), onde `AppComponent` nunca é
 * destruído — a conversa sobrevive a toda navegação.
 *
 * Este host NÃO injeta `ChatService`, e isso é essencial: o construtor daquele
 * serviço dispara `loadSessions`, `loadAgents` e a subscription de aprovações.
 * Numa rota não autenticada isso devolve `Unauthorized`, o `ErrorHandlerService`
 * chama `authService.logout()` e `logout()` faz `window.location.reload()` —
 * loop de reload na tela de entrada. O painel (que provê o serviço) só entra no
 * DOM depois de `available() && activated()`.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IaraFloatingChatPanelComponent, IaraFloatingLauncherComponent],
  selector: 'app-iara-floating-chat',
  standalone: true,
  templateUrl: './iara-floating-chat.component.html',
})
export class IaraFloatingChatComponent {
  protected readonly panel = inject(IaraPanelStateService);

  private readonly auth = inject(AuthService);
  private readonly featureFlags = inject(FeatureFlagService);
  private readonly router = inject(Router);

  /** Muda a cada navegação, para reavaliar se estamos na página `/chat`. */
  private readonly navigationTick = signal(0);

  /**
   * Trava de mão única: liga na primeira abertura e nunca desliga. Assim o
   * `ChatService` não é construído para quem nunca usa o painel, e a conversa
   * sobrevive a fechar/reabrir depois disso.
   */
  private readonly everOpened = signal(false);

  /**
   * O orbe aparece só em tela autenticada, com a flag de chat ligada e fora de
   * `/chat` — lá a superfície completa já está aberta.
   */
  protected readonly available = computed(() => {
    this.navigationTick();

    return (
      this.auth.isAuthenticated &&
      !this.isPublicRoute() &&
      this.featureFlags.isFeatureFlagEnabledSync(FeatureFlagsEnum.FeatureChatAi) &&
      !this.router.url.startsWith(IARA_FLOATING_CHAT_ROUTE_PREFIX)
    );
  });

  /** O painel entra no DOM (e com ele o `ChatService`) só a partir daqui. */
  protected readonly activated = computed(() => this.available() && this.everOpened());

  public constructor() {
    // Preferência persistida de "aberto" conta como abertura — mas só depois de
    // `available()`, então a tela de entrada nunca instancia o serviço.
    if (this.panel.isOpen()) this.everOpened.set(true);

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(),
      )
      .subscribe(() => this.navigationTick.update((tick) => tick + 1));
  }

  protected onToggle(): void {
    this.panel.toggle();
    if (this.panel.isOpen()) this.everOpened.set(true);
  }

  /**
   * Formulários públicos (`data.public`) são voltados ao cliente final e podem ser
   * abertos por alguém logado — a autenticação sozinha não basta para decidir.
   * Mesma travessia de `AuthService.isCurrentRoutePublic`.
   */
  private isPublicRoute(): boolean {
    let snapshot: ActivatedRouteSnapshot | null = this.router.routerState.snapshot.root;

    while (snapshot) {
      if (snapshot.data?.['public']) return true;
      snapshot = snapshot.firstChild;
    }

    return false;
  }
}
